import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../src/app/environments/environment';
import { UserService } from '../../../src/app/service/user.service';
import { escapeHtmlAttribute } from '../html-escape.util';

/**
 * Displays a temporary document in a dialog by generating a secure HTML form that posts to the configured document viewer URL.
 * The component initializes itself on load based on the provided document type and file ID, and then opens the viewer in an embedded dialog.
 * @component
 * @selector app-view-temp-documents
 * @example
 *   <app-view-temp-documents [fileID]="sample_value1" (close)="handler()"></app-view-temp-documents>
 * @input number fileID - The ID of the file to be opened in the document viewer.
 * @input string docType - The document type flag used to determine which viewer flow to use.
 * @input string file_path - The file path for the document, if applicable.
 * @input number | undefined customerIDForWeeklyReport - Optional customer ID used when opening weekly report documents.
 * @input number | undefined userIDForWeeklyReport - Optional user ID used when opening weekly report documents.
 * @input number dataitemcustomer_id - Customer ID used as a fallback for weekly report documents when no explicit customer ID is provided.
 * @output EventEmitter<void> close - Emitted when the dialog is closed.
 * @method ngOnInit - Initializes the component and opens the appropriate document viewer flow based on docType and fileID.
 * @method openDocumentUltimateViewerwhatsnew - Builds and loads a secure POST-based document viewer page for report documents using weekly-report-specific identifiers.
 * @method openDocumentUltimateViewer - Builds and loads a secure POST-based document viewer page using the current user's identifiers.
 * @method onDialogClose - Closes the dialog and emits the close event.
 * @method onDialogOpen - Tags the EJ2 modal container for the shared zoom-proof fullscreen dialog CSS (`:has()` fallback).
 * @lifecycle ngOnInit - Automatically triggers document loading after input properties are set.
 */
@Component({
  selector: 'app-view-temp-documents',
  standalone: true,
  imports: [CommonModule, DialogModule, TranslateModule],
  templateUrl: './view-temp-documents.component.html',
  styleUrl: './view-temp-documents.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewTempDocumentsComponent implements OnDestroy {
  @Input() fileID!: number;
  @Input() docType!: string;
  @Input() file_path!: string;
  @Input() customerIDForWeeklyReport?: number;
  @Input() userIDForWeeklyReport?: number;
  @Input() dataitemcustomer_id!: number;
  @Output() close = new EventEmitter<void>();

  @ViewChild('viewerDialog') viewerDialog?: DialogComponent;

  public isDialogVisible = false;
  public safeDocumentUrl: SafeResourceUrl | null = null;
  public isDocumentViewerReady = false;
  private iframeLoadCount = 0;
  private loaderTimer: ReturnType<typeof setTimeout> | undefined;
  /** Raw blob URL currently bound to the iframe, kept so it can be revoked. */
  private objectUrl: string | null = null;

  constructor(private user: UserService, private sanitizer: DomSanitizer, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {

    if (this.docType === 'R') {
      this.openDocumentUltimateViewerwhatsnew(this.fileID.toString());
    }
    else if (this.fileID) {
      this.openDocumentUltimateViewer(this.fileID.toString());
    }
  }


  /**
  * Opens the document viewer by generating and submitting a temporary HTML form with document metadata.
  * @example
  * penDocumentUltimateViewerwhatsnew(fileId)
  * void
  * @param {string} fileId - The ID of the file to be opened in the document viewer.
  * @returns {void} Does not return a value.
  **/
  openDocumentUltimateViewerwhatsnew(fileId: string) {
    //change
    this.openViewerForm(
      fileId,
      this.customerIDForWeeklyReport ? this.customerIDForWeeklyReport.toString() : this.dataitemcustomer_id.toString()
    );
  }

  /**
   * Opens the document viewer by generating and submitting a hidden HTML form with the required document and user identifiers.
   * @example
   * penDocumentUltimateViewer(fileId)
   * undefined
   * @param {string} fileId - The identifier of the file to be viewed.
   * @returns {void} Does not return a value; updates the component state to display the document viewer.
   **/
  openDocumentUltimateViewer(fileId: string) {
    this.openViewerForm(
      fileId,
      this.customerIDForWeeklyReport ? this.customerIDForWeeklyReport.toString() : this.user.getCustomerId().toString()
    );
  }

  /**
   * Opens the document viewer in the dialog: builds a self-submitting HTML form that POSTs the
   * document and user identifiers to `environment.DOCUMENT_VIEWER_URL`, loads it into the iframe
   * through a blob URL (releasing the previous one) and arms the 30 s reveal fallback.
   * @param fileId - The identifier of the file to be viewed.
   * @param customerId - The customer id to send; each caller resolves its own fallback.
   * @returns {void} Does not return a value; updates the viewer state and opens the dialog.
   */
  private openViewerForm(fileId: string, customerId: string): void {
    const viewerBaseUrl = environment.DOCUMENT_VIEWER_URL;

    const formData: { [key: string]: string } = {
      customer_id: customerId,
      user_id: this.userIDForWeeklyReport ? this.userIDForWeeklyReport.toString() : this.user.getUserId().toString(),
      file_id: fileId,
      doc_type: this.docType
    };

    let formFields = '';
    Object.keys(formData).forEach(key => {
      formFields += `<input type="hidden" name="${escapeHtmlAttribute(key)}" value="${escapeHtmlAttribute(formData[key])}" />`;
    });

    const docFormHtml = `
      <!DOCTYPE html>
      <html><body>
        <form id="docForm" method="POST" action="${viewerBaseUrl}">
          ${formFields}
        </form>
        <script>document.getElementById('docForm').submit();</script>
      </body></html>
    `;

    const blob = new Blob([docFormHtml], { type: 'text/html' });
    // Release the URL from any previous open before replacing it, so the blob
    // document is not retained for the life of the tab.
    this.releaseObjectUrl();
    this.objectUrl = URL.createObjectURL(blob);

    this.safeDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    this.iframeLoadCount = 0;
    this.isDocumentViewerReady = false;
    this.isDialogVisible = true;

    // Safety net: a cross-origin navigation can suppress the iframe's second
    // load event, so reveal the viewer anyway rather than spinning forever.
    clearTimeout(this.loaderTimer);
    this.loaderTimer = setTimeout(() => {
      this.isDocumentViewerReady = true;
      // Fires outside any template event, so OnPush needs an explicit mark.
      this.cdr.markForCheck();
    }, 30000);
  }

  /**
   * Reveals the document once the viewer iframe has finished its second load.
   * The blob page auto-submits its form into the real viewer, so the iframe
   * fires `load` twice: first for the intermediate blob page, then for the
   * viewer's response. Revealing on the first would show an empty frame.
   * @returns Nothing; flips `isDocumentViewerReady` and cancels the fallback timer.
   */
  onIframeLoad(): void {
    this.iframeLoadCount += 1;
    if (this.iframeLoadCount >= 2) {
      clearTimeout(this.loaderTimer);
      this.isDocumentViewerReady = true;
    }
  }

  onDialogClose() {
    this.isDialogVisible = false;
    this.close.emit();
  }

  /**
   * Tags the EJ2-generated modal container with `sfdv-dialog-container` once
   * the dialog opens — the fallback hook the zoom-proof fullscreen dialog
   * rules in styles.css (shared with SfDocumentViewerComponent via the
   * `sfdv-dialog` cssClass) need on browsers without `:has()` support (the
   * container is created by Syncfusion at runtime, so no template class can
   * reach it).
   */
  onDialogOpen(): void {
    const container = this.viewerDialog?.element?.parentElement;
    if (container?.classList.contains('e-dlg-container')) {
      container.classList.add('sfdv-dialog-container');
    }
  }

  /**
   * Revokes the blob URL backing the viewer, if one is still held, so the
   * generated form document is not retained for the life of the tab.
   */
  private releaseObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  /**
   * Cancels the pending fallback reveal timer so it cannot fire against a
   * destroyed component, and releases the blob URL held for the iframe.
   */
  ngOnDestroy(): void {
    clearTimeout(this.loaderTimer);
    this.releaseObjectUrl();
  }
}